import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { LayoutContext } from "contexts/LayoutContext/LayoutContext";
import { MainSurveyStore } from "modules/MainSurveyEditor/hooks/useMainSurvey";
import { SurveyBlocks } from "modules/MainSurveyEditor/surveyBlocks";
import { conditionClient } from "../client/conditionClient";
import { Conditions as ConditionTypes } from "../conditions";
import { ConditionActionTypes } from "../helpers/conditions";
import { computeSharedConditions } from "../helpers/bulkConditions";
import { ConditionsController } from "../components/ConditionsView";

type IdMap = Record<string, number | string>;

export const useBulkConditions = (
  selectedBlocks: SurveyBlocks.Block[],
  mainSurveyStore: Pick<MainSurveyStore, "setIsSaving" | "handleSurveySaved"> & { genericError?: (m: string) => void },
  onSaved?: () => void,
): ConditionsController & { hasExtra: boolean } => {
  const layout = useContext(LayoutContext);
  const genericError = mainSurveyStore.genericError ?? layout.genericError;
  const { setIsSaving, handleSurveySaved } = mainSurveyStore;

  const [conditions, setConditions] = useState<Partial<ConditionTypes.Condition>[]>([]);
  const [idMaps, setIdMaps] = useState<IdMap[]>([]);
  const [hasExtra, setHasExtra] = useState<boolean>(false);
  const [selectPath, setSelectPath] = useState<string>("");

  const selectionKey = useMemo(() => selectedBlocks.map((b) => b.id).join(","), [selectedBlocks]);

  useEffect(() => {
    const { conditions: shared, idMaps: maps, hasExtra: extra } = computeSharedConditions(
      selectedBlocks.map((b) => ({ id: b.id, conditions: b.conditions ?? [] })),
    );
    setConditions(shared);
    setIdMaps(maps);
    setHasExtra(extra);
    setSelectPath("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionKey]);

  const addCondition = useCallback((actionType: ConditionActionTypes) => {
    setConditions((old) => [...old, { actionType }]);
    setIdMaps((old) => [...old, {}]);
  }, []);

  const deleteCondition = useCallback(
    async (index: number) => {
      const map = idMaps[index] ?? {};
      setConditions((old) => old.filter((_, i) => i !== index));
      setIdMaps((old) => old.filter((_, i) => i !== index));

      const entries = selectedBlocks
        .map((b) => ({ block: b, conditionId: map[String(b.id)] }))
        .filter((e) => e.conditionId !== undefined);
      if (!entries.length) return;

      setIsSaving(true);
      const results = await Promise.allSettled(
        entries.map((e) => conditionClient.deleteCondition(e.block.surveyId, e.block.id, e.conditionId!)),
      );
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed) genericError(`Failed to delete from ${failed} of ${entries.length} cells.`);
      handleSurveySaved();
      onSaved?.();
    },
    [idMaps, selectedBlocks, setIsSaving, handleSurveySaved, genericError, onSaved],
  );

  const onUpdate = useCallback(
    async (index: number, conditionUpdate: Partial<ConditionTypes.Condition>) => {
      // merged is derived from the closed-over conditions[index]; onUpdate is in the
      // dep array keyed on conditions, so each render sees the latest. Updates are
      // awaited sequentially in practice, so there's no concurrent-overwrite hazard.
      const merged = { ...conditions[index], ...conditionUpdate };
      setConditions((old) => old.map((c, i) => (i === index ? merged : c)));

      const apiPayload = { ...merged };
      delete (apiPayload as Partial<ConditionTypes.Condition>).contentObject;

      const map = idMaps[index] ?? {};
      const isNew = Object.keys(map).length === 0;
      setIsSaving(true);

      const results = await Promise.allSettled(
        selectedBlocks.map(async (b) => {
          if (isNew) {
            const res = await conditionClient.createCondition(b.surveyId, b.id, apiPayload);
            return { blockId: String(b.id), conditionId: res.data.id };
          }
          const existingId = map[String(b.id)];
          if (existingId === undefined) return null;
          await conditionClient.updateCondition(b.surveyId, b.id, existingId, apiPayload);
          return { blockId: String(b.id), conditionId: existingId };
        }),
      );

      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed) {
        genericError(`Failed to update ${failed} of ${selectedBlocks.length} cells.`);
      }

      if (isNew) {
        const newMap: IdMap = {};
        results.forEach((r) => {
          if (r.status === "fulfilled" && r.value) newMap[r.value.blockId] = r.value.conditionId;
        });
        setIdMaps((old) => old.map((m, i) => (i === index ? newMap : m)));
      }

      handleSurveySaved();
      onSaved?.();
    },
    [conditions, idMaps, selectedBlocks, setIsSaving, handleSurveySaved, genericError, onSaved],
  );

  return {
    conditions,
    addCondition,
    deleteCondition,
    onUpdate,
    selectPath,
    setSelectPath,
    selectGoBackAction: undefined,
    hasExtra,
  };
};
